export type OAuthPopupResult = "success" | "cancel" | "retry" | "closed";

const MESSAGE_RESULTS: Record<string, OAuthPopupResult> = {
    atlassian_oauth_success: "success",
    atlassian_oauth_cancel: "cancel",
    atlassian_oauth_retry: "retry"
};

/**
 * Open the Atlassian OAuth popup (600x700 centered — same geometry as
 * Integrations.tsx) and resolve when it reports back. The backend callback
 * pages post atlassian_oauth_success / _cancel / _retry; "closed" means the
 * window went away without a message (caller should re-check credentials
 * rather than assume failure).
 */
export function openOAuthPopup(url: string): Promise<OAuthPopupResult> {
    return new Promise(resolve => {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
            url,
            "atlassian-oauth",
            `width=${width},height=${height},left=${left},top=${top}`
        );
        if (!popup) {
            resolve("closed");
            return;
        }

        let pollId: ReturnType<typeof setInterval> | null = null;
        const settle = (result: OAuthPopupResult) => {
            window.removeEventListener("message", onMessage);
            if (pollId !== null) clearInterval(pollId);
            resolve(result);
        };

        const onMessage = (event: MessageEvent) => {
            // Source-check pins the sender to our popup so a same-page script or
            // embedded frame can't spoof OAuth completion; success is additionally
            // server-verified by the caller, so we don't need an origin allowlist
            // here (the backend callback origin varies across dev/sandbox/QA).
            if (event.source !== popup) return;
            const type = (event.data as { type?: unknown } | null)?.type;
            const result = typeof type === "string" ? MESSAGE_RESULTS[type] : undefined;
            if (result) settle(result);
        };

        window.addEventListener("message", onMessage);
        pollId = setInterval(() => {
            if (popup.closed) settle("closed");
        }, 500);
    });
}
